import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import Sentry from "./integrations/Sentry";

/**
 * Must be >= the persist layer's `maxAge`. TanStack Query discards restored
 * entries older than `gcTime` on hydrate, so a lower value silently truncates
 * persistence (default is only 5 minutes).
 *
 * The same bound applies to every *per-query* `gcTime` override, not just this
 * default: the persister stays subscribed to the query cache for the whole
 * session and rewrites the snapshot on every cache event, so a query that
 * garbage-collects earlier than `maxAge` is dropped from localStorage the
 * moment it goes unused for its own `gcTime`. A query that genuinely wants a
 * short `gcTime` must opt out of persistence with `meta: { persist: false }`
 * instead.
 */
export const QUERY_CACHE_GC_TIME_MS = 1000 * 60 * 60 * 24; // 24 hours

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 10,
      gcTime: QUERY_CACHE_GC_TIME_MS,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (
          isAxiosError(error) &&
          typeof error?.response?.status === "number" &&
          error.response.status >= 400 &&
          error.response.status < 500
        ) {
          return false;
        }
        if (failureCount >= 3) {
          return false;
        }
        return true;
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(error, {
        extra: {
          query,
        },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context) => {
      Sentry.captureException(error, {
        extra: {
          variables,
          context,
        },
      });
    },
  }),
});

queryClient.setQueryDefaults(["permission"], { gcTime: Infinity });

export { queryClient };
